/**
 * Verification-confidence scoring + review-attention model (FORGE-228).
 *
 * Inspired by "loop engineering" — the practice warns that autonomous loops
 * accrue three silent costs a portal should make visible:
 *   • verification debt   — the loop says "done" without independent proof.
 *   • comprehension rot    — the human's mental model drifts from the code.
 *   • cognitive surrender  — the human stops having an opinion and rubber-stamps.
 *
 * "Reached TESTED" is binary and hides all three. This module turns the signals
 * Forge already records — did an INDEPENDENT reviewer pass, did security check,
 * did the impl land cleanly, did it reach a verified terminal state — into a
 * per-story proof-strength score (0–100) plus attention flags, so a story that
 * was rubber-stamped or shipped unreviewed is visible instead of just "green".
 *
 * Pure + dependency-light: it reads only `agentType` + `status` off the retry
 * clusters (built by retryClusters.ts) plus a few story fields, so both the web
 * app and tests can use it without pulling in the full session type.
 */
import type { RetryCluster } from './retryClusters';

export type GateStatus = 'pass' | 'fail' | 'pending' | 'na';
export type ScoreBand = 'strong' | 'partial' | 'weak' | 'pending' | 'na';

export interface VerificationGate {
  key: 'implemented' | 'reviewed' | 'security' | 'verified_state';
  label: string;
  status: GateStatus;
  weight: number;
}

export interface VerificationScore {
  /** 0–100 weighted pass fraction over applicable gates; null while pending/na. */
  score: number | null;
  band: ScoreBand;
  gates: VerificationGate[];
  /** Attention flags — the human-facing reasons a story deserves a look. */
  flags: VerificationFlag[];
  /** 0–100, higher = more worth a human review (comprehension / surrender guard). */
  risk: number;
}

export type VerificationFlag =
  | 'unreviewed'      // reached a verified state but no INDEPENDENT reviewer passed
  | 'not_verified'    // terminal but failed / blocked / paused
  | 'churned'         // many retries before it settled
  | 'large'           // big change (≥8 SP) — comprehension risk
  | 'force_settled';  // impl only ever force-completed, never a clean pass

const IMPL_ROLES = new Set(['backend', 'web', 'mobile', 'solo']);

function passed(status: RetryCluster['status']): boolean {
  return status === 'passed' || status === 'passed_on';
}
function isReviewRole(agentType: string): boolean {
  return agentType.endsWith('-review');
}

type StateClass = 'verified' | 'failed' | 'skipped' | 'pending';

/** Bucket a story state. `verified` = reached/past TESTED (qa-validate passed). */
export function classifyState(state: string): StateClass {
  const s = (state || '').toUpperCase();
  if (s === 'SKIPPED') return 'skipped';
  if (/(FAILED|BLOCKED|ABORTED|PAUSED|NEEDS_HUMAN|ERROR)/.test(s)) return 'failed';
  if (/(TESTED|READY_FOR_REVIEW|READY_FOR_DEPLOY|DEPLOYING|DEPLOYED|DONE|COMPLETE|MERGED)/.test(s)) {
    return 'verified';
  }
  return 'pending';
}

type StoryLike = { state: string; retries: number; storyPoints: number | null };
type ClusterLike = Pick<RetryCluster, 'agentType' | 'status'>;

const WEIGHTS = { implemented: 25, reviewed: 30, security: 15, verified_state: 30 } as const;

/**
 * Score one story's verification strength from its retry clusters + state.
 * In-flight stories return `band: 'pending'` (no number); code-free stories
 * (SKIPPED, no impl) return `band: 'na'`.
 */
export function scoreStory(story: StoryLike, clusters: ReadonlyArray<ClusterLike>): VerificationScore {
  const sc = classifyState(story.state);

  const implClusters = clusters.filter((c) => IMPL_ROLES.has(c.agentType));
  const reviewClusters = clusters.filter((c) => isReviewRole(c.agentType));
  const secopsClusters = clusters.filter((c) => c.agentType === 'secops');

  const hasImpl = implClusters.length > 0;
  const implPassed = implClusters.some((c) => passed(c.status));
  const reviewPassed = reviewClusters.some((c) => passed(c.status));
  const secopsPassed = secopsClusters.some((c) => passed(c.status));

  // Pending: still in flight → don't score yet.
  if (sc === 'pending') {
    const gates = gatesFor('pending', 'pending', 'pending', 'pending');
    return { score: null, band: 'pending', gates, flags: [], risk: 0 };
  }
  // Skipped / no code → not applicable.
  if (sc === 'skipped' || !hasImpl) {
    const gates = gatesFor('na', 'na', 'na', 'na');
    return { score: null, band: 'na', gates, flags: [], risk: 0 };
  }

  const implStatus: GateStatus = implPassed ? 'pass' : 'fail';
  // Review is always expected on a terminal code story — absence IS the debt.
  const reviewStatus: GateStatus = reviewPassed ? 'pass' : 'fail';
  // Security only applies when the pipeline ran secops for this stack.
  const secStatus: GateStatus = secopsClusters.length === 0 ? 'na' : secopsPassed ? 'pass' : 'fail';
  const stateStatus: GateStatus = sc === 'verified' ? 'pass' : 'fail';

  const gates = gatesFor(implStatus, reviewStatus, secStatus, stateStatus);

  const applicable = gates.filter((g) => g.status === 'pass' || g.status === 'fail');
  const denom = applicable.reduce((s, g) => s + g.weight, 0);
  const num = applicable.filter((g) => g.status === 'pass').reduce((s, g) => s + g.weight, 0);
  const score = denom > 0 ? Math.round((num / denom) * 100) : null;

  const flags: VerificationFlag[] = [];
  if (sc === 'verified' && !reviewPassed) flags.push('unreviewed');
  if (sc === 'failed') flags.push('not_verified');
  if ((story.retries ?? 0) >= 3) flags.push('churned');
  if ((story.storyPoints ?? 0) >= 8) flags.push('large');
  if (hasImpl && !implPassed) flags.push('force_settled');

  const band: ScoreBand =
    score === null ? 'na' : score >= 80 ? 'strong' : score >= 55 ? 'partial' : 'weak';

  // Risk: low proof + size + churn + unreviewed. Higher = review this first.
  let risk = Math.round((100 - (score ?? 0)) * 0.6);
  if (flags.includes('unreviewed')) risk += 20;
  if (flags.includes('not_verified')) risk += 25;
  if (flags.includes('large')) risk += 12;
  else if ((story.storyPoints ?? 0) >= 5) risk += 6;
  if (flags.includes('churned')) risk += 15;
  else if ((story.retries ?? 0) >= 1) risk += 5;
  risk = Math.max(0, Math.min(100, risk));

  return { score, band, gates, flags, risk };
}

function gatesFor(
  implemented: GateStatus,
  reviewed: GateStatus,
  security: GateStatus,
  verified_state: GateStatus,
): VerificationGate[] {
  return [
    { key: 'implemented', label: 'Implemented cleanly', status: implemented, weight: WEIGHTS.implemented },
    { key: 'reviewed', label: 'Independently reviewed', status: reviewed, weight: WEIGHTS.reviewed },
    { key: 'security', label: 'Security checked', status: security, weight: WEIGHTS.security },
    { key: 'verified_state', label: 'Reached verified state', status: verified_state, weight: WEIGHTS.verified_state },
  ];
}

/** Short human label for a flag — used in the "what to review" surface. */
export function flagLabel(flag: VerificationFlag): string {
  switch (flag) {
    case 'unreviewed': return 'shipped without an independent review';
    case 'not_verified': return 'did not reach a verified state';
    case 'churned': return 'many retries before it settled';
    case 'large': return 'large change (≥8 SP)';
    case 'force_settled': return 'impl never cleanly passed';
  }
}
